import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { FaTruck, FaHome, FaArrowLeft } from "react-icons/fa";

const NotFound = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(15);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1500);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <TheNotFound>
      <div className="notfound-container">
        <div className="error-code">
          <span className="code-404">4</span>
          <div className="animated-truck">
            <FaTruck />
            <div className="truck-trail"></div>
          </div>
          <span className="code-404">4</span>
        </div>

        <h1>Page Not Found</h1>
        <p>Oops! The page you're looking for seems to have taken a detour.</p>

        <div className="countdown-section">
          <div className="countdown-timer">
            <svg className="progress-ring" width="80" height="80">
              <circle
                className="progress-ring-circle-bg"
                stroke="#e2e8f0"
                strokeWidth="4"
                fill="transparent"
                r="36"
                cx="40"
                cy="40"
              />
              <circle
                className="progress-ring-circle"
                stroke="#D4AF37"
                strokeWidth="4"
                fill="transparent"
                r="36"
                cx="40"
                cy="40"
                style={{
                  strokeDasharray: `${2 * Math.PI * 36}`,
                  strokeDashoffset: `${2 * Math.PI * 36 * (1 - countdown / 10)}`,
                }}
              />
            </svg>
            <div className="countdown-number">{countdown}</div>
          </div>
          <p className="redirect-message">
            Redirecting to homepage in <strong>{countdown}</strong> second
            {countdown !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="action-buttons">
          <Link to="/" className="home-btn">
            <FaHome /> Go Back Home Now
          </Link>
          <button onClick={() => navigate(-1)} className="back-btn">
            <FaArrowLeft /> Go Back to Previous Page
          </button>
        </div>

        <div className="help-text">
          <p>
            Need assistance? <Link to="/contact">Contact our support team</Link>
          </p>
        </div>
      </div>
    </TheNotFound>
  );
};

const TheNotFound = styled.div`
  width: 100%;
  min-height: 80vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9edf2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding:0 2rem;
  overflow: hidden;

  .notfound-container {
    text-align: center;
    max-width: 600px;
    width: 100%;
    animation: fadeInUp 0.6s ease-out;
  }

  .error-code {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2rem;

    .code-404 {
      font-size: 6rem;
      font-weight: 800;
      color: #0b1e33;
      text-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
    }

    .animated-truck {
      position: relative;
      animation: truckMove 2s ease-in-out infinite;

      svg {
        font-size: 4rem;
        color: #d4af37;
        filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.2));
      }

      .truck-trail {
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 60px;
        height: 2px;
        background: linear-gradient(90deg, #d4af37, transparent);
        animation: trailExpand 2s ease-in-out infinite;
      }
    }
  }

  h1 {
    font-size: 2.5rem;
    color: #0b1e33;
    margin-bottom: 1rem;
    font-weight: 700;
  }

  p {
    font-size: 1rem;
    color: #718096;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .countdown-section {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    .countdown-timer {
      position: relative;
      display: inline-block;
      margin-bottom: 1rem;

      .progress-ring {
        transform: rotate(-90deg);

        .progress-ring-circle {
          transition: stroke-dashoffset 0.1s linear;
        }
      }

      .countdown-number {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 2rem;
        font-weight: 800;
        color: #d4af37;
      }
    }

    .redirect-message {
      margin-bottom: 0;
      font-size: 0.95rem;

      strong {
        color: #d4af37;
        font-size: 1.1rem;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;

    .home-btn,
    .back-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-radius: 10px;
      font-size: 0.95rem;
      font-weight: 600;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.3s ease;
      font-family: inherit;

      &:hover {
        transform: translateY(-2px);
      }
    }

    .home-btn {
      background: #d4af37;
      color: #0b1e33;
      border: none;

      &:hover {
        background: #c4a12e;
        box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
      }
    }

    .back-btn {
      background: transparent;
      color: #0b1e33;
      border: 1px solid #d4af37;

      &:hover {
        background: rgba(212, 175, 55, 0.1);
        border-color: #c4a12e;
      }
    }
  }

  .help-text {
    p {
      font-size: 0.85rem;
      margin-bottom: 0;

      a {
        color: #d4af37;
        text-decoration: none;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes truckMove {
    0% {
      transform: translateX(-10px);
    }
    50% {
      transform: translateX(10px);
    }
    100% {
      transform: translateX(-10px);
    }
  }

  @keyframes trailExpand {
    0% {
      width: 20px;
      opacity: 0.5;
    }
    50% {
      width: 60px;
      opacity: 1;
    }
    100% {
      width: 20px;
      opacity: 0.5;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    .error-code {
      .code-404 {
        font-size: 4rem;
      }

      .animated-truck svg {
        font-size: 3rem;
      }
    }

    h1 {
      font-size: 1.8rem;
    }

    p {
      font-size: 0.9rem;
    }

    .action-buttons {
      flex-direction: column;

      .home-btn,
      .back-btn {
        justify-content: center;
        width: 100%;
      }
    }

    .countdown-section {
      padding: 1.5rem;
    }
  }

  @media screen and (max-width: 480px) {
    .error-code {
      gap: 0.5rem;

      .code-404 {
        font-size: 3rem;
      }

      .animated-truck svg {
        font-size: 2.5rem;
      }
    }

    h1 {
      font-size: 1.5rem;
    }
  }
`;

export default NotFound;
